import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orders as ordersApi, auth, payments } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import PayPalPaymentButton from '../components/PayPalPaymentButton';
import { 
  CreditCard, 
  Package, 
  DollarSign, 
  Loader2, 
  ArrowLeft,
  Receipt,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const PaymentPage = () => {
  usePageTitle('Make Payment');
  const { user, updateUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showPayPal, setShowPayPal] = useState(false);
  const [paymentMode, setPaymentMode] = useState('full'); // 'full' or 'selected'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordersRes, profileRes] = await Promise.all([
        ordersApi.getAll({ limit: 100 }),
        auth.getProfile()
      ]);
      // Filter to only unpaid/pending orders
      const unpaidOrders = ordersRes.data.orders.filter(
        order => order.status !== 'cancelled' && !order.is_paid
      );
      setOrders(unpaidOrders);
      updateUser(profileRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPayPal(false);
    setSelectedOrders([]);
    toast.success('Payment completed successfully!');
    // Reload data
    await loadData();
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const selectAllOrders = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.id));
    }
  };

  const creditUsed = user?.credit_used || 0;
  
  // Calculate selected orders total
  const selectedTotal = orders
    .filter(o => selectedOrders.includes(o.id))
    .reduce((sum, o) => sum + (o.total || 0), 0);

  // Amount to pay based on mode
  const amountToPay = paymentMode === 'full' ? creditUsed : selectedTotal;

  if (loading) {
    return (
      <div className="container-wide py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-8">
      {/* Header */}
      <div className="mb-6">
        <Link to="/dashboard" className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Make a Payment</h1>
        <p className="text-gray-500">Pay your outstanding balance or select specific orders</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Payment Options */}
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Payment Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pay Full Balance */}
              <div 
                className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                  paymentMode === 'full' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => { setPaymentMode('full'); setShowPayPal(false); }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMode === 'full' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}>
                      {paymentMode === 'full' && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Pay Full Balance</p>
                      <p className="text-sm text-gray-500">Clear your entire outstanding balance</p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-blue-600">${creditUsed.toFixed(2)}</p>
                </div>
              </div>

              {/* Pay Selected Orders */}
              <div 
                className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                  paymentMode === 'selected' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => { setPaymentMode('selected'); setShowPayPal(false); }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMode === 'selected' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}>
                      {paymentMode === 'selected' && <CheckCircle className="w-4 h-4 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Pay Selected Orders</p>
                      <p className="text-sm text-gray-500">Choose specific orders to pay</p>
                    </div>
                  </div>
                  {paymentMode === 'selected' && selectedOrders.length > 0 && (
                    <p className="text-xl font-bold text-blue-600">${selectedTotal.toFixed(2)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Order Selection (when "selected" mode) */}
          {paymentMode === 'selected' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-gray-600" />
                    Select Orders to Pay
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={selectAllOrders}>
                    {selectedOrders.length === orders.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {orders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No unpaid orders found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.map(order => (
                      <div 
                        key={order.id}
                        className={`p-4 border rounded-lg cursor-pointer transition ${
                          selectedOrders.includes(order.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => toggleOrderSelection(order.id)}
                      >
                        <div className="flex items-center gap-4">
                          <Checkbox 
                            checked={selectedOrders.includes(order.id)}
                            onCheckedChange={() => toggleOrderSelection(order.id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-gray-900">{order.order_number}</p>
                              <p className="font-bold text-gray-900">${order.total?.toFixed(2)}</p>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                              <span>{new Date(order.created_at).toLocaleDateString()}</span>
                              <span>{order.items?.length || 0} items</span>
                              <Badge variant={order.status === 'delivered' ? 'default' : 'secondary'}>
                                {order.status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Payment Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Payment Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Details */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Total Outstanding:</span>
                  <span>${creditUsed.toFixed(2)}</span>
                </div>
                {paymentMode === 'selected' && (
                  <div className="flex justify-between text-gray-600">
                    <span>Selected Orders:</span>
                    <span>{selectedOrders.length} order(s)</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg border-t pt-2">
                  <span>Amount to Pay:</span>
                  <span className="text-green-600">${amountToPay.toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Button / PayPal */}
              {amountToPay > 0 ? (
                !showPayPal ? (
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => setShowPayPal(true)}
                    disabled={paymentMode === 'selected' && selectedOrders.length === 0}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Proceed to Payment
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <PayPalPaymentButton 
                      amount={amountToPay}
                      orderIds={paymentMode === 'selected' ? selectedOrders : null}
                      onSuccess={handlePaymentSuccess}
                      onError={() => setShowPayPal(false)}
                    />
                    <Button
                      variant="ghost"
                      onClick={() => setShowPayPal(false)}
                      className="w-full text-gray-500"
                    >
                      Cancel
                    </Button>
                  </div>
                )
              ) : (
                <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="font-medium">No Outstanding Balance</p>
                  <p className="text-sm">You're all caught up!</p>
                </div>
              )}

              {/* Info Note */}
              <p className="text-xs text-gray-500 text-center">
                Secure payment powered by PayPal. Processing fees may apply.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
